import type {
  DevToolsSegmentedControlOption,
  DevToolsSegmentedControlProps,
} from 'Modules/softphone/components/SoftphoneDevTools/DevToolsSegmentedControl/index.types';
import {
  chipClasses,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * DevToolsSegmentedControl - Pick one of a few sibling views
 *
 * The panel is 570px wide, so a record cannot show its overview, its events,
 * its media and its devices at once.
 */
export const DevToolsSegmentedControl = <TValue extends string>({
  options,
  value,
  onChange,
  label,
  variant = 'track',
  className,
}: DevToolsSegmentedControlProps<TValue>): React.ReactElement => (
  <div
    role="tablist"
    aria-label={label}
    className={cn(
      'flex',
      variant === 'track'
        ? 'items-stretch gap-[3px] rounded-[9px] border border-zinc-800 bg-zinc-900 p-[3px]'
        : 'flex-wrap items-center gap-1.5',
      className
    )}
  >
    {options.map((option: DevToolsSegmentedControlOption<TValue>) => {
      const isActive = option.value === value;

      if (variant === 'inline') {
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(option.value)}
            className={chipClasses(isActive)}
          >
            {option.label}
            {option.badge !== undefined && (
              <span className="font-sp-mono text-[10px] opacity-70">
                {option.badge}
              </span>
            )}
          </button>
        );
      }

      return (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(option.value)}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[7px] px-2 py-1.5',
            'text-[11.5px] transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
            isActive
              ? 'bg-zinc-900 font-semibold text-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.18)]'
              : 'font-medium text-zinc-400 hover:text-zinc-200'
          )}
        >
          {option.label}
          {option.badge !== undefined && (
            <span className="font-sp-mono text-[10px] text-zinc-400">
              {option.badge}
            </span>
          )}
        </button>
      );
    })}
  </div>
);
